import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UrlService } from 'app/core/http/url.service';
import { stringWrapper } from 'app/core/kh-utils/kh-utils';
import { WrappedNumber } from 'app/core/kh-utils/wrapper-number';
import { StudentHomeInfo } from 'app/student/home/models/student-home-info.model';
import { AddClassRequestResponseModel } from 'app/student/models/add-class-request-response.model';
import { JoinClassDetailsModel } from 'app/student/models/join-class-details.model';
import { ParentStatusInfoModel } from 'app/student/models/parent-status-info.model';
import { RejoinClassRequestResponseModel } from 'app/student/models/rejoin-class-request-response.model';
import { RemindCredentialsInfoModel } from 'app/student/models/remind-credentials-info.model';
import { IStudentClassInfo } from 'app/student/models/student-class-info';
import { ClassmateInfo } from 'app/student/models/student-info';
import { StudentRequestResponse } from 'app/student/models/student-request-response';

@Injectable({
  providedIn: 'root'
})
export class StudentDataService {
  public constructor(
    private readonly httpClient: HttpClient,
    private readonly urlService: UrlService) { }

  public getHomeInfo(classId: string): Observable<StudentHomeInfo> {
    const url = this.urlService.buildServerApiUrl('Student/Home', classId);
    return this.httpClient.get<StudentHomeInfo>(url);
  }

  public requestGameShow(classId: string): Observable<StudentRequestResponse> {
    const url = this.urlService.buildServerApiUrl('Student/Home', classId, 'RequestGameShow');
    return this.httpClient.post<StudentRequestResponse>(url, null);
  }

  public getClass(classId: string): Observable<IStudentClassInfo> {
    const url = this.urlService.buildServerApiUrl('Student/Classes', classId);
    return this.httpClient.get<IStudentClassInfo>(url);
  }

  public createAccountByEmail(registerEmail: string): Observable<string> {
    const url = this.urlService.buildServerApiUrl('Student/Parents/RegisterFreeParent');
    const body = stringWrapper(registerEmail);
    return this.httpClient.post<{ ParentAccountId: string }>(url, body).pipe(map(data => data?.ParentAccountId ?? ''));
  }

  public getRemindCredentialsInfo(): Observable<RemindCredentialsInfoModel> {
    const url = this.urlService.buildServerApiUrl('Student/RemindCredentialsInfo');
    return this.httpClient.get<RemindCredentialsInfoModel>(url);
  }

  public getParentStatus(classId: string): Observable<ParentStatusInfoModel> {
    const url = this.urlService.buildServerApiUrl('Student/Parents/Status');
    const params = { ClassId: classId };
    return this.httpClient.get<ParentStatusInfoModel>(url, { params });
  }

  public addClass(classId: string, courseId: string): Observable<AddClassRequestResponseModel> {
    const url = this.urlService.buildServerApiUrl('Student/Classes', classId, courseId);
    return this.httpClient.post<AddClassRequestResponseModel>(url, {});
  }

  public getClassDetails(classPin: string): Observable<JoinClassDetailsModel> {
    const url = this.urlService.buildServerApiUrl('Student/Registration', classPin, 'ClassDetails');
    return this.httpClient.get<JoinClassDetailsModel>(url);
  }

  public rejoinClass(classPin: string): Observable<RejoinClassRequestResponseModel> {
    const url = this.urlService.buildServerApiUrl('Student/Classes/Rejoin', classPin);
    return this.httpClient.post<RejoinClassRequestResponseModel>(url, {});
  }

  public getNumUnviewedNotification(): Observable<number> {
    const url = this.urlService.buildServerApiUrl('Student/NumUnviewedNotification');
    return this.httpClient.get<WrappedNumber>(url).pipe(map(data => data.Value));
  }

  public removeParent(parentId: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Student', 'Parent', parentId);
    return this.httpClient.delete<void>(url);
  }

  public getClassmates(classId: string): Observable<ClassmateInfo[]> {
    const url = this.urlService.buildServerApiUrl('Student/Class', classId, 'Classmates');
    return this.httpClient.get<ClassmateInfo[]>(url);
  }

  public updateActiveClass(classId: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Student', 'Classes', 'ActiveClass');
    return this.httpClient.post<void>(url, null, { params: { classId } });
  }
}
